import { useState } from 'react';
import { isMobileDevice } from '@optimus-web/common';
import { useId } from '@optimus-web/helpers';
import { Checkbox } from '../Checkbox';
import { RadioButton } from '../Radio';
import { Typography } from '../typography';
import { Label, LabelContainer, OptionTypeContainer, SelectionCardContainer, SelectionCardMediaElement } from './style';
import { Direction, SelectionCardProps } from './types';

export const SelectionCard = ({
    checked,
    description,
    $direction,
    disabled,
    media,
    label,
    onChange,
    type,
    value,
    name,
    dataTestId,
    cornerRadius = 'medium',
    showSelector = true,
}: SelectionCardProps) => {
    const [focused, setFocused] = useState(false);
    const labelId = useId('selection-card-label');
    const descriptionId = useId('selection-card-description');

    const getTemplateColumns = (direction: Direction) => {
        if (direction === 'horizontal') {
            if (!showSelector) {
                return 'auto max-content';
            }
            if (media) {
                return 'max-content auto max-content';
            }
            return 'max-content auto';
        }
        return 'auto';
    };

    const handleFocus = () => {
        setFocused(true);
    };

    const handleBlur = () => {
        setFocused(false);
    };

    const selectorProps = {
        name,
        checked,
        value,
        disabled,
        onFocus: handleFocus,
        onBlur: handleBlur,
        'aria-labelledby': labelId,
        'aria-describedby': description ? descriptionId : undefined,
    };

    return (
        <SelectionCardContainer
            as="label"
            checked={checked}
            $direction={$direction}
            disabled={disabled}
            gap="200"
            autoFlow={$direction === 'vertical' ? 'row' : 'column'}
            alignItems={$direction === 'vertical' ? 'start' : 'center'}
            templateColumns={getTemplateColumns($direction)}
            data-test-id={dataTestId}
            cornerRadius={cornerRadius}
            focusVisible={focused && !showSelector && !disabled}
            onClick={type === 'radio' ? handleBlur : undefined}
            aria-disabled={disabled}
        >
            <OptionTypeContainer $direction={$direction} showSelector={showSelector}>
                {type === 'checkbox' ? (
                    <Checkbox {...selectorProps} onChange={onChange} />
                ) : (
                    <RadioButton {...selectorProps} onChange={onChange} />
                )}
            </OptionTypeContainer>
            {media ? (
                <SelectionCardMediaElement
                    itemAlignment="center"
                    alignment="center"
                    order={$direction === 'horizontal' ? 1 : 0}
                >
                    {media}
                </SelectionCardMediaElement>
            ) : null}
            <LabelContainer
                vertical
                itemAlignment="start"
                alignment="start"
                spacing={$direction === 'horizontal' ? `25` : `50`}
            >
                <Label
                    aria-hidden
                    as="div"
                    textStyle={isMobileDevice() ? 'bodyMediumStrong' : 'bodyLargeStrong'}
                    id={labelId}
                >
                    {label}
                </Label>
                {description ? (
                    <Typography aria-hidden textStyle="bodyMedium" variant="tertiary" as="div" id={descriptionId}>
                        {description}
                    </Typography>
                ) : null}
            </LabelContainer>
        </SelectionCardContainer>
    );
};
